import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  IoAddOutline,
  IoAlertCircle,
  IoCheckmarkCircle,
  IoChevronDown,
  IoFlashOutline,
  IoSaveOutline,
  IoTrashOutline,
} from 'react-icons/io5';

import { confirmDialog } from '../../components/ConfirmDialog';
import {
  deleteLlmChatModel,
  deleteLlmConnection,
  deleteLlmModel,
  fetchLlmSettings,
  saveLlmAccessLevel,
  saveLlmChatModel,
  saveLlmConnection,
  saveLlmModel,
  saveLlmUsage,
  testLlmModel,
} from '../../services/api';

// Section 1: connections (provider + keys) and the models on each. A model
// is added by name + ID; its Test finds the API format, the efforts it
// accepts and whether it reads images, and saving keeps what the test found.
// Section 2: for each feature, pick one of those models and an effort.
// Keys are write-only: the API never returns them, so key fields start empty.

const ACCESS_LEVELS = ['basic', 'paid', 'premium'];
const EFFORT_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
};
const FORMAT_LABELS = { claude: 'Claude format', openai: 'OpenAI format' };
const SECRET_LABELS = {
  api_key: 'API key',
  access_key_id: 'Access key ID',
  secret_access_key: 'Secret access key',
};

const input =
  'w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:border-indigo-500 focus:outline-none';
const button =
  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50';
const primary = `${button} bg-indigo-600 text-white hover:bg-indigo-500`;
const secondary = `${button} border border-zinc-700 text-zinc-300 hover:bg-zinc-800`;
const danger = `${button} border border-red-900 text-red-400 hover:bg-red-950`;
const badge = 'rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300';

const errorText = (error) =>
  (typeof error === 'string' && error) ||
  error?.message ||
  error?.detail ||
  'Something went wrong';

const confirmed = (title, message, confirmLabel = 'Delete') =>
  confirmDialog({ title, message, confirmLabel, danger: true });

export const countLlmProblems = (settings) =>
  Object.values(settings?.problems || {}).reduce(
    (total, group) => total + Object.keys(group || {}).length,
    0,
  );

const Problem = ({ text }) =>
  text ? (
    <span className="inline-flex items-center gap-1 text-xs text-amber-400">
      <IoAlertCircle className="shrink-0" /> {text}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
      <IoCheckmarkCircle /> Ready
    </span>
  );

const Section = ({ title, hint, children, action }) => (
  <section className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-5">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-zinc-100">{title}</h3>
        {hint && <p className="mt-1 text-sm text-zinc-500">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

// Native select with room for its own chevron (the browser's arrow sat on
// the right edge). `groups` renders <optgroup>s.
const Select = ({
  value,
  onChange,
  options = [],
  groups,
  placeholder,
  ...rest
}) => (
  <div className="relative w-full">
    <select
      className={`${input} appearance-none pr-9 disabled:opacity-50`}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {groups
        ? groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ))
        : options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
    </select>
    <IoChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500" />
  </div>
);

// One row's local edits. Saving any row reloads the settings, which rebuilds
// every saved object; reset only when this row's own saved content changed,
// so unsaved edits in other rows survive.
const useRowValue = (saved) => {
  const key = JSON.stringify(saved);
  const [value, setValue] = useState(saved);
  useEffect(() => setValue(JSON.parse(key)), [key]);
  return [value, setValue];
};

// --- Section 1: connections and their models -----------------------------

const blankConnection = {
  name: '',
  provider: 'azure',
  auth: 'key',
  resource: '',
  region: '',
};

const ConnectionForm = ({
  id,
  connection,
  secretFields,
  onSave,
  onDelete,
  onCancel,
}) => {
  const [form, setForm] = useState({ ...blankConnection, ...connection });
  // New values only; never prefilled. null = remove the stored key.
  const [secrets, setSecrets] = useState({});
  const [busy, setBusy] = useState(false);
  const fields = secretFields[`${form.provider}:${form.auth}`] || [];
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    const sent = Object.fromEntries(
      Object.entries(secrets).filter(
        ([name, v]) => fields.includes(name) && v !== '',
      ),
    );
    const removed = Object.keys(sent).filter((n) => sent[n] === null);
    const replaced = Object.keys(sent).filter(
      (n) => sent[n] !== null && connection?.secrets?.[n]?.set,
    );
    if (
      (removed.length || replaced.length) &&
      !(await confirmDialog({
        title: removed.length
          ? 'Remove the stored key?'
          : 'Replace the stored key?',
        message: `${[...removed, ...replaced].map((n) => SECRET_LABELS[n] || n).join(', ')} on "${form.name}". Features using this connection stop working until a valid key is saved.`,
        confirmLabel: removed.length ? 'Remove' : 'Replace',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    try {
      const { secrets: _shown, ...plain } = form;
      await onSave(id, { ...plain, secrets: sent });
      setSecrets({});
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md bg-zinc-900/60 p-4">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="space-y-1 text-sm text-zinc-400">
          Name
          <input
            className={input}
            value={form.name}
            onChange={(e) => set('name')(e.target.value)}
            placeholder="e.g. Azure Foundry"
          />
        </label>
        <div className="space-y-1 text-sm text-zinc-400">
          Provider
          <Select
            aria-label="Provider"
            value={form.provider}
            onChange={(v) =>
              setForm((f) => ({ ...f, provider: v, auth: 'key' }))
            }
            options={[
              { value: 'azure', label: 'Microsoft Azure Foundry' },
              { value: 'bedrock', label: 'Amazon Bedrock' },
            ]}
          />
        </div>
        {form.provider === 'azure' ? (
          <label className="space-y-1 text-sm text-zinc-400">
            Foundry resource name
            <input
              className={input}
              value={form.resource || ''}
              onChange={(e) => set('resource')(e.target.value)}
              placeholder="my-resource (from my-resource.services.ai.azure.com)"
            />
          </label>
        ) : (
          <>
            <label className="space-y-1 text-sm text-zinc-400">
              Region
              <input
                className={input}
                value={form.region || ''}
                onChange={(e) => set('region')(e.target.value)}
                placeholder="us-east-1"
              />
            </label>
            <div className="space-y-1 text-sm text-zinc-400">
              Sign-in
              <Select
                aria-label="Sign-in"
                value={form.auth}
                onChange={set('auth')}
                options={[
                  { value: 'iam', label: 'IAM role of the server (no key)' },
                  { value: 'key', label: 'Access key' },
                ]}
              />
            </div>
          </>
        )}
      </div>
      {fields.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {fields.map((name) => {
            const stored =
              connection?.secrets?.[name]?.set && secrets[name] !== null;
            return (
              <label key={name} className="space-y-1 text-sm text-zinc-400">
                <span className="flex items-center justify-between">
                  {SECRET_LABELS[name] || name}
                  {secrets[name] === null ? (
                    <span className="text-xs text-red-400">
                      Will be removed on save
                    </span>
                  ) : stored ? (
                    <span className="text-xs text-emerald-400">
                      Set — type to replace
                    </span>
                  ) : (
                    <span className="text-xs text-amber-400">! Not set</span>
                  )}
                </span>
                <div className="flex gap-2">
                  <input
                    className={input}
                    type="password"
                    autoComplete="new-password"
                    spellCheck={false}
                    value={secrets[name] || ''}
                    onChange={(e) =>
                      setSecrets((s) => ({ ...s, [name]: e.target.value }))
                    }
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    placeholder={stored ? '••••••••' : 'Paste the key'}
                  />
                  {connection?.secrets?.[name]?.set && (
                    <button
                      type="button"
                      className={danger}
                      title="Remove the stored key"
                      onClick={() =>
                        setSecrets((s) => ({ ...s, [name]: null }))
                      }
                    >
                      <IoTrashOutline />
                    </button>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={primary}
          disabled={busy || !form.name}
          onClick={save}
        >
          <IoSaveOutline /> Save connection
        </button>
        {onCancel && (
          <button type="button" className={secondary} onClick={onCancel}>
            Cancel
          </button>
        )}
        {id && (
          <button type="button" className={danger} onClick={() => onDelete(id)}>
            <IoTrashOutline /> Delete
          </button>
        )}
      </div>
    </div>
  );
};

const Detected = ({ found, tested }) => (
  <div className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-400">
    <span className={badge}>{FORMAT_LABELS[found.family] || found.family}</span>
    <span className={badge}>
      Effort:{' '}
      {found.efforts?.length
        ? found.efforts.map((e) => EFFORT_LABELS[e] || e).join(', ')
        : 'none'}
    </span>
    <span className={badge}>{found.vision ? 'Reads images' : 'Text only'}</span>
    <span className={badge}>
      {found.thinking === 'always'
        ? 'Always thinks'
        : found.thinking === 'optional'
          ? `Thinking on/off${
              found.thinking_off_efforts?.length
                ? ` (off: ${found.thinking_off_efforts
                    .map((e) => EFFORT_LABELS[e] || e)
                    .join(', ')})`
                : ''
            }`
          : 'No thinking'}
    </span>
    <span
      className={
        tested || found.tested_at ? 'text-emerald-400' : 'text-zinc-500'
      }
    >
      {tested
        ? 'Found by test'
        : found.tested_at
          ? `Passed test ${new Date(
              // Stored as UTC; Mongo hands it back without the zone.
              /Z|[+-]\d\d:\d\d$/.test(found.tested_at)
                ? found.tested_at
                : `${found.tested_at}Z`,
            ).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`
          : 'Not tested yet'}
    </span>
  </div>
);

// Name + model ID. Test finds the rest; a new or changed model ID can only
// be saved after it passes.
const ModelRow = ({
  modelKey,
  model,
  provider,
  problem,
  userIdToken,
  onSave,
  onDelete,
  onCancel,
}) => {
  const [value, setValue] = useRowValue(model);
  const [result, setResult] = useState(null); // last passed test
  const [testing, setTesting] = useState(false);

  const modelId = (value.model_id || '').trim();
  const idChanged = !modelKey || modelId !== model.model_id;
  const testedNow = result && result.model_id === modelId;
  const canSave = value.name?.trim() && modelId && (!idChanged || testedNow);

  const test = async () => {
    setTesting(true);
    try {
      const found = await testLlmModel(userIdToken, {
        connection_id: model.connection_id,
        model_id: modelId,
      });
      if (found?.ok) {
        setResult({ ...found, model_id: modelId });
        toast.success(`Model answered: "${found.reply || '(empty)'}"`);
      } else {
        setResult(null);
        toast.error(`Test failed: ${found?.error || 'no answer'}`, {
          duration: 8000,
        });
      }
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-1.5 py-2">
      <div className="grid items-center gap-2 md:grid-cols-[1fr_1.3fr_auto]">
        <input
          aria-label="Model name"
          className={input}
          value={value.name || ''}
          onChange={(e) => setValue({ ...value, name: e.target.value })}
          placeholder="Name, e.g. Claude Haiku 5.5"
        />
        <input
          aria-label="Model ID"
          className={input}
          value={value.model_id || ''}
          onChange={(e) => setValue({ ...value, model_id: e.target.value })}
          placeholder={
            provider === 'azure' ? 'Deployment name' : 'Bedrock model ID'
          }
        />
        <div className="flex gap-2">
          <button
            type="button"
            className={secondary}
            disabled={testing || !modelId}
            title="Tiny test call"
            aria-label="Tiny test call"
            onClick={test}
          >
            <IoFlashOutline /> {testing ? 'Testing…' : 'Test'}
          </button>
          <button
            type="button"
            className={primary}
            disabled={!canSave}
            title={canSave ? 'Save' : 'Test the model first'}
            aria-label="Save model"
            onClick={() =>
              onSave(modelKey, {
                ...value,
                model_id: modelId,
                proof: testedNow ? result.proof : undefined,
              })
            }
          >
            <IoSaveOutline />
          </button>
          {modelKey ? (
            <button
              type="button"
              className={danger}
              aria-label="Delete model"
              onClick={() => onDelete(modelKey, value.name)}
            >
              <IoTrashOutline />
            </button>
          ) : (
            <button type="button" className={secondary} onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>
      {testedNow ? (
        <Detected found={result} tested />
      ) : idChanged ? (
        <span className="text-xs text-zinc-500">
          Run Test to find its format, efforts and image support.
        </span>
      ) : (
        <Detected found={model} />
      )}
      {problem && <Problem text={problem} />}
    </div>
  );
};

// --- Section 2: which model each feature uses ------------------------------

// A use or chat model's own status. A missing key is shown once, on its
// connection, so a row waiting on its connection shows nothing (not "Ready").
const RowStatus = ({ problem, target, settings }) => {
  const connectionId = settings.models?.[target?.model]?.connection_id;
  if (!problem && settings.problems?.connections?.[connectionId]) return null;
  return <Problem text={problem} />;
};

// One dropdown, grouped by connection; effort lists only what that model
// accepts.
const ModelPicker = ({ value, onChange, settings }) => {
  const models = settings.models || {};
  const groups = Object.entries(settings.connections || {})
    .map(([cid, c]) => ({
      label: c.name,
      options: Object.entries(models)
        .filter(([, m]) => m.connection_id === cid)
        // The connection repeats in the label: a closed select shows only
        // the option, and two connections can hold the same model.
        .map(([key, m]) => ({ value: key, label: `${m.name} · ${c.name}` })),
    }))
    .filter((g) => g.options.length);
  const efforts = models[value.model]?.efforts || [];
  const pickModel = (key) => {
    const accepted = models[key]?.efforts || [];
    const effort = accepted.includes(value.effort)
      ? value.effort
      : accepted.includes('low')
        ? 'low'
        : accepted[0] || null;
    onChange({ ...value, model: key || null, effort });
  };
  return (
    <>
      <Select
        aria-label="Model"
        value={value.model}
        onChange={pickModel}
        groups={groups}
        placeholder="Select a model…"
      />
      <Select
        aria-label="Effort"
        value={efforts.includes(value.effort) ? value.effort : ''}
        onChange={(v) => onChange({ ...value, effort: v })}
        options={efforts.map((e) => ({
          value: e,
          label: EFFORT_LABELS[e] || e,
        }))}
        placeholder={
          !value.model
            ? 'Pick a model first'
            : efforts.length
              ? 'Effort…'
              : 'No effort setting'
        }
        disabled={!efforts.length}
      />
    </>
  );
};

const UsageRow = ({ usageKey, label, saved, problem, settings, onSave }) => {
  const [value, setValue] = useRowValue(saved || { effort: 'low' });
  return (
    <div className="grid items-center gap-2 border-b border-zinc-800/70 py-3 md:grid-cols-[1.5fr_1.4fr_0.9fr_auto]">
      <div>
        <div className="text-sm font-medium text-zinc-200">{label}</div>
        <RowStatus problem={problem} target={saved} settings={settings} />
      </div>
      <ModelPicker value={value} onChange={setValue} settings={settings} />
      <button
        type="button"
        className={primary}
        disabled={!value.model}
        aria-label="Save"
        onClick={() => onSave(usageKey, value)}
      >
        <IoSaveOutline />
      </button>
    </div>
  );
};

const ChatModelRow = ({ entry, problem, settings, onSave, onDelete }) => {
  const [value, setValue] = useRowValue(entry);
  return (
    <div className="space-y-2 border-b border-zinc-800/70 py-3">
      <div className="grid gap-2 md:grid-cols-[1fr_2fr]">
        <input
          aria-label="Name users see"
          className={input}
          value={value.label || ''}
          onChange={(e) => setValue({ ...value, label: e.target.value })}
          placeholder="Name users see, e.g. Claude Haiku"
        />
        <input
          aria-label="Description"
          className={input}
          value={value.description || ''}
          onChange={(e) => setValue({ ...value, description: e.target.value })}
          placeholder="Short description"
        />
      </div>
      <div className="grid items-center gap-2 md:grid-cols-[1.4fr_0.9fr_auto]">
        <ModelPicker value={value} onChange={setValue} settings={settings} />
        <div className="flex gap-2">
          <button
            type="button"
            className={primary}
            disabled={!value.label || !value.model}
            aria-label="Save chat model"
            onClick={() => onSave(value)}
          >
            <IoSaveOutline />
          </button>
          {entry.id && (
            <button
              type="button"
              className={danger}
              aria-label="Delete chat model"
              onClick={() => onDelete(entry.id, entry.label)}
            >
              <IoTrashOutline />
            </button>
          )}
        </div>
      </div>
      {entry.id && (
        <RowStatus problem={problem} target={entry} settings={settings} />
      )}
    </div>
  );
};

const AccessLevelRow = ({ level, saved, chatModels, problem, onSave }) => {
  const [value, setValue] = useRowValue(saved || { models: [], default: null });
  const toggle = (id) =>
    setValue((v) => {
      const models = v.models.includes(id)
        ? v.models.filter((m) => m !== id)
        : [...v.models, id];
      return { models, default: models.includes(v.default) ? v.default : null };
    });
  return (
    <div className="grid items-center gap-3 border-b border-zinc-800/70 py-3 md:grid-cols-[0.8fr_2fr_1fr_auto]">
      <div>
        <div className="text-sm font-medium capitalize text-zinc-200">
          {level}
        </div>
        <Problem text={problem} />
      </div>
      <div className="flex flex-wrap gap-3">
        {chatModels.map((m) => (
          <label
            key={m.id}
            className="flex items-center gap-1.5 text-sm text-zinc-300"
          >
            <input
              type="checkbox"
              checked={value.models.includes(m.id)}
              onChange={() => toggle(m.id)}
            />
            {m.label}
          </label>
        ))}
        {!chatModels.length && (
          <span className="text-sm text-zinc-500">Add chat models first.</span>
        )}
      </div>
      <Select
        aria-label="Default chat model"
        value={value.default}
        onChange={(v) => setValue({ ...value, default: v || null })}
        options={chatModels
          .filter((m) => value.models.includes(m.id))
          .map((m) => ({ value: m.id, label: m.label }))}
        placeholder="Default…"
      />
      <button
        type="button"
        className={primary}
        aria-label="Save access level"
        onClick={() => onSave(level, value)}
      >
        <IoSaveOutline />
      </button>
    </div>
  );
};

// --- Panel -----------------------------------------------------------------

const LlmSettingsPanel = ({ userIdToken, onSettings }) => {
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null); // connection id, 'new' or null
  const [addingModelTo, setAddingModelTo] = useState(null); // connection id
  const [draftChat, setDraftChat] = useState(null);

  const apply = useCallback(
    (next) => {
      if (!next) return;
      setSettings(next);
      onSettings?.(next);
    },
    [onSettings],
  );

  useEffect(() => {
    if (!userIdToken) return;
    fetchLlmSettings(userIdToken)
      .then(apply)
      .catch((error) => toast.error(errorText(error)));
  }, [userIdToken, apply]);

  const run = async (action, done) => {
    try {
      apply(await action());
      if (done) toast.success(done);
      return true;
    } catch (error) {
      toast.error(errorText(error));
      return false;
    }
  };

  if (!settings)
    return <p className="text-sm text-zinc-500">Loading AI settings…</p>;

  const connections = Object.entries(settings.connections || {});
  const models = Object.entries(settings.models || {});
  const problems = settings.problems || {};
  const saveModel = (key, value) =>
    run(() => saveLlmModel(userIdToken, key, value), 'Model saved');

  return (
    <div className="space-y-6">
      <Section
        title="1. Connections and their models"
        hint="Provider accounts, their keys and the models on each. Keys are stored encrypted and are never shown again, even to admins."
        action={
          editing !== 'new' && (
            <button
              type="button"
              className={secondary}
              onClick={() => setEditing('new')}
            >
              <IoAddOutline /> Add connection
            </button>
          )
        }
      >
        <div className="space-y-4">
          {editing === 'new' && (
            <div className="rounded-md border border-zinc-800">
              <ConnectionForm
                secretFields={settings.secret_fields}
                onCancel={() => setEditing(null)}
                onSave={async (_, body) =>
                  (await run(
                    () => saveLlmConnection(userIdToken, null, body),
                    'Connection saved',
                  )) && setEditing(null)
                }
              />
            </div>
          )}
          {connections.map(([id, c]) => (
            <div key={id} className="rounded-md border border-zinc-800">
              {editing === id ? (
                <ConnectionForm
                  id={id}
                  connection={c}
                  secretFields={settings.secret_fields}
                  onCancel={() => setEditing(null)}
                  onSave={async (cid, body) =>
                    (await run(
                      () => saveLlmConnection(userIdToken, cid, body),
                      'Connection saved',
                    )) && setEditing(null)
                  }
                  onDelete={async (cid) =>
                    (await confirmed(
                      `Delete the connection "${c.name}"?`,
                      'Its stored keys are deleted with it. A connection that still has models cannot be deleted.',
                    )) &&
                    (await run(
                      () => deleteLlmConnection(userIdToken, cid),
                      'Connection deleted',
                    )) &&
                    setEditing(null)
                  }
                />
              ) : (
                <div className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="text-sm font-medium text-zinc-200">
                      {c.name}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {c.provider === 'azure'
                        ? `Azure Foundry · ${c.resource || 'resource not set'}`
                        : `Bedrock · ${c.region || 'region not set'} · ${c.auth === 'iam' ? 'IAM role' : 'access key'}`}
                    </div>
                    <Problem text={problems.connections?.[id]} />
                  </div>
                  <button
                    type="button"
                    className={secondary}
                    onClick={() => setEditing(id)}
                  >
                    Edit
                  </button>
                </div>
              )}
              <div className="border-t border-zinc-800/70 px-4 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-zinc-500">
                    Models
                  </span>
                  {addingModelTo !== id && (
                    <button
                      type="button"
                      className={secondary}
                      onClick={() => setAddingModelTo(id)}
                    >
                      <IoAddOutline /> Add model
                    </button>
                  )}
                </div>
                <div className="divide-y divide-zinc-800/60">
                  {models
                    .filter(([, m]) => m.connection_id === id)
                    .map(([key, m]) => (
                      <ModelRow
                        key={key}
                        modelKey={key}
                        model={m}
                        provider={c.provider}
                        problem={problems.models?.[key]}
                        userIdToken={userIdToken}
                        onSave={saveModel}
                        onDelete={async (k, name) =>
                          (await confirmed(
                            `Delete the model "${name}"?`,
                            `From ${c.name}. A model still used by a feature or a chat model cannot be deleted.`,
                          )) &&
                          run(
                            () => deleteLlmModel(userIdToken, k),
                            'Model deleted',
                          )
                        }
                      />
                    ))}
                  {addingModelTo === id && (
                    <ModelRow
                      model={{ connection_id: id, name: '', model_id: '' }}
                      provider={c.provider}
                      userIdToken={userIdToken}
                      onCancel={() => setAddingModelTo(null)}
                      onSave={async (key, value) =>
                        (await saveModel(key, value)) && setAddingModelTo(null)
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
          {!connections.length && editing !== 'new' && (
            <p className="text-sm text-amber-400">
              ! No connections yet. Add one to turn on AI features.
            </p>
          )}
        </div>
      </Section>

      <Section
        title="2. Where each model is used"
        hint="Pick a model and how hard it thinks. Each model lists only the effort levels it accepts."
      >
        <div className="hidden gap-2 pb-2 text-xs uppercase tracking-wide text-zinc-500 md:grid md:grid-cols-[1.5fr_1.4fr_0.9fr_auto]">
          <span>Use</span>
          <span>Model</span>
          <span>Effort</span>
          <span />
        </div>
        {Object.entries(settings.usage_catalog || {}).map(([key, label]) => (
          <UsageRow
            key={key}
            usageKey={key}
            label={label}
            saved={settings.usages?.[key]}
            problem={problems.usages?.[key]}
            settings={settings}
            onSave={(k, target) =>
              run(() => saveLlmUsage(userIdToken, k, target), 'Saved')
            }
          />
        ))}
      </Section>

      <Section
        title="Chat models"
        hint="The models users can pick in chat."
        action={
          !draftChat && (
            <button
              type="button"
              className={secondary}
              onClick={() => setDraftChat({ effort: 'low' })}
            >
              <IoAddOutline /> Add chat model
            </button>
          )
        }
      >
        {(settings.chat_models || []).map((entry) => (
          <ChatModelRow
            key={entry.id}
            entry={entry}
            problem={problems.chat_models?.[entry.id]}
            settings={settings}
            onSave={(value) =>
              run(() => saveLlmChatModel(userIdToken, value), 'Saved')
            }
            onDelete={async (id, label) =>
              (await confirmed(
                `Remove "${label}" from the chat picker?`,
                'Users who picked it fall back to their access level’s default.',
                'Remove',
              )) && run(() => deleteLlmChatModel(userIdToken, id), 'Removed')
            }
          />
        ))}
        {draftChat && (
          <ChatModelRow
            entry={draftChat}
            settings={settings}
            onSave={async (value) =>
              (await run(
                () => saveLlmChatModel(userIdToken, { ...value, id: '' }),
                'Added',
              )) && setDraftChat(null)
            }
          />
        )}
      </Section>

      <Section
        title="Who can use which chat model"
        hint="Each access level needs a default."
      >
        {ACCESS_LEVELS.map((level) => (
          <AccessLevelRow
            key={level}
            level={level}
            saved={settings.access_levels?.[level]}
            chatModels={settings.chat_models || []}
            problem={problems.access_levels?.[level]}
            onSave={(l, value) =>
              run(() => saveLlmAccessLevel(userIdToken, l, value), 'Saved')
            }
          />
        ))}
      </Section>
    </div>
  );
};

export default LlmSettingsPanel;
